import { Badge, Button, Input, Select } from 'antd';
import { ColumnsType, ColumnType } from 'antd/es/table';
import { SortOrder } from 'antd/es/table/interface';
import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { HiSortAscending, HiSortDescending } from 'react-icons/hi';
import { IoSearch } from 'react-icons/io5';
import { StyledHeaderBar, StyledTable, TableWrapper } from './style';
import { TableProps } from './types';
import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
} from '@heroicons/react/16/solid';
import { TableProps as AntdTableProps } from 'antd/es/table';
import { useTheme } from '@src/theme';
import { AllColumnsHidden } from './components';
import { useTranslation } from 'react-i18next';
import { PAGE_SIZES } from '@src/constants/pagination';

function withDefaultSortIcon<T>(cols: ColumnsType<T>): ColumnsType<T> {
    return cols.map((col) => {
        if (!col.sorter) return col;

        return {
            ...col,
            sortIcon: ({ sortOrder }: { sortOrder: SortOrder }) =>
                sortOrder === 'ascend' ? (
                    <HiSortAscending
                        size={18}
                        className={`${sortOrder === 'ascend' ? 'text-primaryBlue' : 'text-primaryBlack'} mt-1`}
                    />
                ) : (
                    <HiSortDescending
                        size={18}
                        className={`${sortOrder === 'descend' ? 'text-primaryBlue' : 'text-primaryBlack'} mt-1`}
                    />
                ),
        } as ColumnType<T>;
    });
}

export function Table({
    title,
    count,
    dataSource = [],
    columns,
    onSearch,
    searchPlaceholder = 'Search…',
    actions,
    selectable,
    selectedRowKeys,
    onSelect,
    showPagination = true,
    paginationProps = {},
    onPageChange,
    loading,
    onChange,
    customEmpty,
    customEmptyText = 'No data',
    footer,
    showSummary = true,
    summaryFormat = 'Showing {{start}}-{{end}} of {{total}} entries',
    rowKey,
    rowClassName,
    className,
    activeFilter = false,
    activeFilerContent,
    setFilterOpen,
    filterOpen = false,
    headerActions,
    headerClassName,
    hasFilter = true,
    searchValue,
    onRow,
    onRowClick,
    allColumnsHidden,
    onShowAllColumns,
    filters,
    onClearFilters,
    ...props
}: TableProps) {
    const { t } = useTranslation();
    const { theme } = useTheme();
    const [value, setValue] = useState('');

    const [pageSize, setPageSize] = useState(paginationProps?.pageSize ?? 25);
    const defaultPage = paginationProps?.defaultCurrent ?? 1;
    const [currentPage, setCurrentPage] = useState<number>(defaultPage);

    const enhancedColumns = withDefaultSortIcon(columns || []);

    useEffect(() => {
        if (paginationProps.current) {
            setCurrentPage(paginationProps.current);
        }
    }, [paginationProps.current]);

    useEffect(() => {
        if (paginationProps?.pageSize) {
            setPageSize(paginationProps.pageSize);
        }
    }, [paginationProps?.pageSize]);

    const total = paginationProps.total ?? dataSource.length;
    const start = total > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const end = Math.min(currentPage * pageSize, total);
    const totalPages = Math.ceil(total / pageSize);

    const summaryText = summaryFormat
        .replace('{{start}}', start.toString())
        .replace('{{end}}', end.toString())
        .replace('{{total}}', total?.toString());

    const handlePageChange = (page: number, size: number) => {
        setCurrentPage(page);
        onPageChange?.(page, size);
        onSelect?.([]);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setValue(newValue);
        onSearch?.(e);
        onSelect?.([]);
    };

    const handlePageSizeChange = (newSize: number) => {
        setPageSize(newSize);
        setCurrentPage(1);

        onPageChange?.(1, newSize);
        onSelect?.([]);
    };

    const getKey = (record: string): React.Key =>
        typeof rowKey === 'function'
            ? rowKey(record)
            : (record as any)[rowKey ?? 'key'];

    const handleSelectChange = (record: any, selected: boolean) => {
        const key = getKey(record);
        const newKeys = selected
            ? [...(selectedRowKeys ?? []), key]
            : (selectedRowKeys ?? []).filter((k) => k !== key);

        onSelect?.(newKeys);
    };

    const handleSelectAll = (selected: boolean, allRows: any[]) => {
        const newKeys = selected ? allRows.map(getKey) : [];
        onSelect?.(newKeys);
    };

    const rowSelection = selectable
        ? {
              selectedRowKeys: selectedRowKeys ?? [],
              onSelect: handleSelectChange,
              onSelectAll: handleSelectAll,
          }
        : undefined;

    const handleFilterClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setFilterOpen?.(!filterOpen);
    };

    const activeFilterCount = useMemo(() => {
        return Object.entries(filters ?? {}).filter(([_, value]) => {
            if (value === null || value === '' || value === undefined) {
                return false;
            }
            if (Array.isArray(value) && value.length === 0) {
                return false;
            }
            return true;
        }).length;
    }, [filters]);

    const renderFilterIcon = () => {
        return (
            <Badge
                count={activeFilterCount}
                size="small"
                offset={[-4, 4]}
                style={{
                    backgroundColor: 'var(--color-primary)',
                    color: '#fff',
                }}
            >
                <Button
                    onClick={handleFilterClick}
                    className="btn-secondary"
                    icon={
                        <FunnelIcon
                            width={16}
                            height={16}
                            className="text-textPrimary"
                        />
                    }
                />
            </Badge>
        );
    };

    const renderPageNumbers = () => {
        const maxPagesToShow = 5;
        const pages: (number | '...')[] = [];

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            const startPage = Math.max(2, currentPage - 1);
            const endPage = Math.min(totalPages - 1, currentPage + 1);

            pages.push(1);

            if (startPage > 2) pages.push('...');

            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }

            if (endPage < totalPages - 1) pages.push('...');

            pages.push(totalPages);
        }

        return pages.map((p, index) =>
            p === '...' ? (
                <span key={`ellipsis-${index}`} className="px-2">
                    ...
                </span>
            ) : (
                <Button
                    key={p}
                    className={`w-6 h-6 p-1 ${
                        currentPage === p ? 'border-blue-500 text-blue-500' : ''
                    }`}
                    onClick={() => handlePageChange(p, pageSize)}
                >
                    {p}
                </Button>
            )
        );
    };

    return (
        <TableWrapper className={clsx(className)}>
            {(title || actions || onSearch) && (
                <StyledHeaderBar className="flex flex-col md:flex-row gap-2 w-full">
                    <div
                        className="flex flex-col md:flex-row items-center space-x-2 w-full"
                        style={{ color: theme.semantic.textPrimary }}
                    >
                        {title && <span className="text-M-20">{title}</span>}
                    </div>
                    <div
                        className={clsx(
                            'flex flex-col md:flex-row items-center gap-2 justify-end w-full mb-2',
                            headerClassName
                        )}
                    >
                        {onSearch && (
                            <>
                                {hasFilter && activeFilterCount > 0 && (
                                    <Button
                                        className="btn-secondary"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onClearFilters?.();
                                        }}
                                        icon={
                                            <XMarkIcon width={16} height={16} />
                                        }
                                    >
                                        {t('components.table.clearFilters')}
                                    </Button>
                                )}

                                <Input
                                    prefix={
                                        hasFilter ? renderFilterIcon() : null
                                    }
                                    suffix={
                                        !value && (
                                            <IoSearch
                                                size={16}
                                                style={{
                                                    color: theme.semantic
                                                        .primary,
                                                }}
                                            />
                                        )
                                    }
                                    variant="outlined"
                                    className={`w-full sm:w-[360px] !pr-3 ${activeFilter ? '!pl-1 !py-[4px]' : '!pl-3 !py-[8px]'}`}
                                    placeholder={searchPlaceholder}
                                    allowClear
                                    onChange={handleChange}
                                    value={searchValue}
                                    style={{
                                        boxShadow: 'none',
                                        outline: 'none',
                                        borderColor: theme.semantic.border,
                                        backgroundColor: theme.semantic.surface,
                                    }}
                                />
                            </>
                        )}
                        {actions}
                    </div>
                </StyledHeaderBar>
            )}
            {headerActions && <div className="mb-2">{headerActions}</div>}

            {allColumnsHidden ? (
                <TableWrapper className="ant-table-container">
                    <AllColumnsHidden
                        t={t}
                        onShowAll={onShowAllColumns || (() => {})}
                    />
                </TableWrapper>
            ) : (
                <StyledTable
                    {...props}
                    columns={enhancedColumns}
                    dataSource={dataSource}
                    scroll={{ x: 'max-content' }}
                    loading={{
                        spinning:
                            typeof loading === 'boolean' ? loading : !!loading,
                        className: 'h-full !max-h-[unset]',
                        indicator: (
                            <AiOutlineLoading3Quarters
                                size={24}
                                className="animate-spin"
                                style={{
                                    color: theme.semantic.primary,
                                }}
                            />
                        ),
                    }}
                    pagination={false}
                    rowKey={rowKey}
                    rowClassName={(record, index) =>
                        clsx(
                            typeof rowClassName === 'function'
                                ? rowClassName(record, index)
                                : rowClassName,
                            onRowClick && 'cursor-pointer'
                        )
                    }
                    locale={{ emptyText: customEmpty ?? customEmptyText }}
                    onChange={onChange}
                    rowSelection={rowSelection}
                    onRow={(record, index) => {
                        const parentOnRow =
                            (props as AntdTableProps<typeof record>).onRow?.(
                                record,
                                index
                            ) || {};

                        const ourOnClick = () => onRowClick?.(record, index);

                        return {
                            ...parentOnRow,
                            onClick: parentOnRow.onClick
                                ? (event) => {
                                      parentOnRow.onClick?.(event);
                                      ourOnClick();
                                  }
                                : ourOnClick,
                        };
                    }}
                />
            )}

            {(showSummary || showPagination) && (
                <div className="text-textPrimary flex justify-between flex-col md:flex-row items-center mt-4 px-2 text-sm">
                    {showSummary && <div>{summaryText}</div>}

                    {showPagination && dataSource.length > 0 && (
                        <div className="flex flex-col md:flex-row items-center gap-2">
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-textSecondary">
                                    {t(
                                        'components.table.rowsPerPage',
                                        'Rows per page'
                                    )}
                                </span>

                                <Select
                                    size="small"
                                    value={pageSize}
                                    style={{ width: 60 }}
                                    onChange={handlePageSizeChange}
                                    options={PAGE_SIZES.map((size) => ({
                                        label: size,
                                        value: size,
                                    }))}
                                />
                            </div>
                            <div className="flex space-x-1">
                                <Button
                                    className="w-6 h-6 p-1"
                                    disabled={currentPage === 1}
                                    onClick={() =>
                                        handlePageChange(1, pageSize)
                                    }
                                >
                                    <ChevronDoubleLeftIcon className="w-4 h-4" />
                                </Button>

                                <Button
                                    type="text"
                                    className="w-6 h-6 p-1"
                                    disabled={currentPage === 1}
                                    onClick={() =>
                                        handlePageChange(
                                            currentPage - 1,
                                            pageSize
                                        )
                                    }
                                >
                                    <ChevronLeftIcon className="w-4 h-4" />
                                </Button>

                                {renderPageNumbers()}
                                <Button
                                    type="text"
                                    className="w-6 h-6 p-1"
                                    disabled={currentPage === totalPages}
                                    onClick={() =>
                                        handlePageChange(
                                            currentPage + 1,
                                            pageSize
                                        )
                                    }
                                >
                                    <ChevronRightIcon width={16} height={16} />
                                </Button>

                                <Button
                                    className="w-6 h-6 p-1"
                                    disabled={currentPage === totalPages}
                                    onClick={() =>
                                        handlePageChange(totalPages, pageSize)
                                    }
                                >
                                    <ChevronDoubleRightIcon
                                        width={16}
                                        height={16}
                                    />
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
            {footer}
        </TableWrapper>
    );
}
